import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { verifiedSignUp } from "@/lib/auth.functions";
import { Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/signup")({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) throw redirect({ to: "/" });
  },
  head: () => ({
    meta: [
      { title: "Create your Pluggz account" },
      { name: "description", content: "Join Pluggz to save pieces from creator storefronts, follow your favorite tastemakers, and apply to launch your own curated shop." },
      { property: "og:title", content: "Create your Pluggz account" },
      { property: "og:description", content: "Save picks from creator storefronts and follow tastemakers on Pluggz." },
      { property: "og:url", content: `${SITE_URL}/signup` },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/signup` }],
  }),
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    const recaptchaToken = await getRecaptchaToken("signup");
    const result = await verifiedSignUp({
      data: {
        email,
        password,
        displayName,
        emailRedirectTo: window.location.origin,
        recaptchaToken,
      },
    });
    if (!result.ok) {
      setLoading(false);
      return setError(result.error);
    }
    if (result.session) {
      const { error: setErr } = await supabase.auth.setSession(result.session);
      setLoading(false);
      if (setErr) return setError(setErr.message);
      navigate({ to: "/creator-dashboard" });
    } else {
      setLoading(false);
      setMessage("Check your inbox to confirm your email.");
    }
  }

  async function oauth(provider: "google" | "apple") {
    setError(null);
    const result = await lovable.auth.signInWithOAuth(provider, { redirect_uri: window.location.origin });
    if (result.error) return setError(result.error.message ?? "Sign-up failed");
    if (result.redirected) return;
    navigate({ to: "/" });
  }

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden px-4 py-12 sm:px-6 sm:py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-48 w-48 rounded-full bg-accent/20 blur-3xl float-slow sm:h-64 sm:w-64" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-sand/30 blur-3xl float-slow sm:h-64 sm:w-64" style={{ animationDelay: "-3s" }} />
      <div className="relative">
        <p className="editorial-eyebrow">Join Pluggz</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
          Create <span className="italic shimmer">account</span>
        </h1>

        <button
          type="button"
          onClick={() => oauth("google")}
          disabled={loading}
          className="mt-8 flex w-full items-center justify-center gap-2 border border-border bg-card/80 px-6 py-3 text-sm uppercase tracking-widest text-foreground backdrop-blur transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase tracking-widest">
            <span className="bg-background px-3 text-muted-foreground">or</span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="signup-name" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Display name</label>
            <input id="signup-name" required autoComplete="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="How you'd like to appear" className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground sm:text-sm" />
          </div>
          <div>
            <label htmlFor="signup-email" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
            <input id="signup-email" required type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground sm:text-sm" />
          </div>
          <div>
            <label htmlFor="signup-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Password</label>
            <div className="relative">
              <input id="signup-password" required type={showPassword ? "text" : "password"} autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 pr-10 text-base outline-none transition-colors focus:border-foreground sm:text-sm" />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password.length > 0 && (
              <div className="mt-2 flex gap-1">
                <div className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 8 ? "bg-red-500" : "bg-muted"}`} />
                <div className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 12 ? "bg-amber-500" : "bg-muted"}`} />
                <div className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 16 ? "bg-green-500" : "bg-muted"}`} />
              </div>
            )}
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          {message && <p className="text-xs text-accent">{message}</p>}
          <button disabled={loading} type="submit" className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="link-underline text-foreground">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
